import axios from 'axios';
import type { DateTime } from 'luxon';

export default class SurveysService
{
    public static async getList(filter: FilterModel, pager: Pager)
    {
        return (await axios.get<Pagination<Resource<ListItemModel>>>('admin/cms/surveys', {
            params: { ...filter, ...pager.data() }
        })).data;
    }

    public static async fetch(id: number)
    {
        return (await axios.get<Resource<FormModel>>(`admin/cms/surveys/${id}`)).data;
    }

    public static async create(model: UpsertModel)
    {
        return (await axios.post<Resource<FormModel>>(`admin/cms/surveys`, model)).data;
    }

    public static async update(id: number, model: UpsertModel)
    {
        return (await axios.put<Resource<FormModel>>(`admin/cms/surveys/${id}`, model)).data;
    }

    public static async remove(id: number)
    {
        return (await axios.delete(`admin/cms/surveys/${id}`)).data as Statement;
    }

    public static async status(id: number, status: PublicationEnum)
    {
        return (await axios.patch(`admin/cms/surveys/${id}/status`, {
            status: status
        })).data;
    }

    public static async publish(id: number, model: PublishModel)
    {
        return (await axios.patch(`admin/cms/surveys/${id}/publish`, model)).data;
    }

    public static async attach(id: number, pageId: number)
    {
        return (await axios.post(`admin/cms/surveys/${id}/page/${pageId}`)).data;
    }

    public static async detach(id: number, pageId: number)
    {
        return (await axios.delete(`admin/cms/surveys/${id}/page/${pageId}`)).data;
    }

    public static async getVersions(id: number, pager: Pager)
    {
        return (await axios.get<Pagination<EntityVersion>>(`admin/cms/surveys/${id}/versions`, {
            params: pager.data()
        })).data;
    }

    public static async getPagePublications(id: number)
    {
        return (await axios.get<PagePublication[]>(`admin/cms/surveys/${id}/pages`)).data;
    }

    public static async results(originId: number)
    {
        return (await axios.get<VoteListModel[]>(`admin/cms/surveys/results/${originId}`)).data;
    }

    public static async getReport(originId: number)
    {
        return (await axios.get<Blob>(`admin/cms/surveys/report/${originId}`, { responseType: 'blob' })).data;
    }

    public static async getAutocompleteList(search: string, limit: number = 10)
    {
        return (await axios.get<ListItemModel[]>(`admin/cms/surveys/autocomplete`, {
            params: {
                search: search,
                limit: limit
            }
        })).data;
    }

    public static async fetchAutocompleteModel(id: number)
    {
        return (await axios.get<ListItemModel>(`admin/cms/surveys/autocomplete/${id}`)).data;
    }

    public static async fetchAutocompleteModelByOriginId(originId: number)
    {
        return (await axios.get<ListItemModel>(`admin/cms/surveys/autocomplete/${originId}/origin`)).data;
    }

    public static async fetchSurveySummary(id: number)
    {
        return (await axios.get<SurveySummaryModel>(`admin/dashboards/widgets/survey/${id}`)).data;
    }
}

export interface ListItemModel
{
    id: number;
    originId: number;
    dateCreatedUtc: DateTime;
    dateModifiedUtc: DateTime;
    createdBy: string;
    modifiedBy: string;
    question: string;
    status: PublicationEnum;
    revisionOrigin: number;
    ownerId: number;
    resourceId: number;
    answersCount: number;
}

export interface FilterModel
{
    pageId?: number;
    userId: number;
    status: string;
    question: string;
    dateFromUtc?: DateTime;
    dateDueUtc?: DateTime;
    onlyMine: boolean;
    onlyTeam: boolean;
    isActive: boolean;
}

export interface UpsertModel
{
    id: number;
    question: string;
    answers: SurveyAnswer[];
    meta: MetaSettingsModel | null;
}

export interface SurveyAnswer
{
    id: number;
    text: string;
}

export interface FormModel extends UpsertModel
{
    datePublishedUtc?: DateTime;
    dateDismissedUtc?: DateTime;
    status: PublicationEnum;
}

export interface PublishModel
{
    datePublishedUtc?: DateTime;
    dateDismissedUtc?: DateTime;
}

export interface VoteListModel
{
    answerId: number;
    answerName: string;
    percent: number;
    votes: number;
}

export interface SurveySummaryModel
{
    id: number;
    question: string;
    answers: SurveyAnswerModel;
}

export interface SurveyAnswerModel
{
    id: number;
    answer: string;
    count: number;
}

export interface SurveySummaryList
{
    survey: SurveySummaryModel;
}
